import { Card } from '@payaca/ambrosia';
import { FC, useMemo, type JSX } from 'react';

import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';

import { useBusinessAccountForm } from './useBusinessAccountForm';
import { useGettingPaidFormValidators } from '@/hooks/useCompanyInfoValidators';
import SettingsSaveButton from './SettingsSaveButton';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';

type Props = {
  readOnly: boolean;
};

type FormState = {
  vatNumber: string | null;
};

const CompanySettingsVatNumber: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const account = useAccount();
  const region = useRegion();

  const vatRegionalLabel = useMemo(
    () => getRegionalTextString(region, RegionalStrings.VALUE_ADDED_TAX_NUMBER),
    [region]
  );

  const initialState: FormState = {
    vatNumber: account.vatNumber ?? null,
  };

  const fieldValidators = useGettingPaidFormValidators(region);

  const {
    formState,
    updateFormFields,
    formValidationResult,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, fieldValidators);

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-2">
        <h2>{vatRegionalLabel}</h2>
        <ValidatedFieldWrapper
          validationResult={formValidationResult.vatNumber}
        >
          <BasicField
            name="vatNumber"
            label={vatRegionalLabel}
            value={formState.vatNumber}
            styleVariant={InputStyleVariant.OUTSIZE}
            onChange={updateFormFields}
            textBefore={
              [AccountRegions.UK].includes(region) ? 'GB-' : undefined
            }
            isDisabled={readOnly}
          />
        </ValidatedFieldWrapper>
        {!readOnly && (
          <SettingsSaveButton
            isDirty={isDirty}
            label="Save VAT number"
            isSaving={isSaving}
            isValid={isValid}
            onSave={saveAndFlush}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsVatNumber;
